import { SearchAggregationBy, SearchCategories } from 'src/app/components/search/search.enum';
import { MapLocation, MapState } from 'src/app/interfaces/map.interface';
import {
  LocationDetailsParams,
  RequestAttributeParams,
  SearchAggregateAttributes,
  SearchAttributes,
  SearchRequestObject,
} from 'src/app/interfaces/search.interface';
import { Operator } from 'src/app/swagger-api';
import { CommonMapSearchValues } from '../../../../common/common-map-search.values';
import { MapFilters, NestedBucketIndividualRole } from '../../map.enum';
import {
  isInAggregationCountryZoomRange,
  isInAggregationRegionZoomRange,
  isInAggregationSubregionZoomRange,
} from '../../map.helpers';
import MapSettings from '../../map.settings';
import { MapValues } from '../../map.values';
import MarkersMappers from '../markers.mappers';

export class MapSearchRequestCreatorFactory {
  /**
   * @description Creates a search request object for aggregation searching on the map.
   * @param { MapState } state
   * @returns { SearchRequestObject }
   */
  public static createAggregatedMapSearchRequest(state: MapState): SearchRequestObject {
    return {
      aggregate_attributes: this.getAggregateAttributes(state),
      search_attributes: this.getSearchAttrs({ state, applyLocationAttrs: false, aggregated: true }),
    };
  }

  /**
   * @description Creates a search request object to fetch a covid statistics info.
   * @param { MapState } state
   * @returns { SearchRequestObject }
   */
  public static createCovidMapSearchRequest(state: MapState): SearchRequestObject {
    return {
      aggregate_attributes: [
        {
          name: CommonMapSearchValues.SEARCH_ATTRIBUTE_GEO_TILE_GRID,
          value: { precision: state.zoom },
        },
      ],
      search_attributes: this.getLocationDetailsAttrs({
        state: { ...state, form_location: null },
        applyLocationAttrs: true,
      }),
    };
  }

  /**
   * @description Creates a search request object for non aggregation searching on the map.
   * @param { MapState } state
   * @param {boolean} applyLocationAttrs
   * @param {string} locationLiteral
   * @returns { SearchRequestObject }
   */
  public static createMapSearchRequest(
    state: MapState,
    applyLocationAttrs: boolean,
    locationLiteral?: string,
  ): SearchRequestObject {
    return {
      search_attributes: this.getSearchAttrs({ state, applyLocationAttrs, aggregated: false, locationLiteral }),
      sort_attributes: [{ name: CommonMapSearchValues.SORT_ATTRIBUTE_SCORE, ascending: false }],
    };
  }

  /**
   * @description Creates a search request object for statistics under the map.
   * @param { MapState } state
   * @param { boolean } applyDefaultFilters
   * @returns { SearchRequestObject }
   */
  public static createMapStatisticsSearchRequest(state: MapState, applyDefaultFilters: boolean): SearchRequestObject {
    return {
      search_attributes: applyDefaultFilters
        ? this.getSearchAttrs({ state, applyLocationAttrs: false })
        : [this.getSearchItems(state), this.getExcludeEmptyLocationFilter()],
    };
  }

  /**
   * @description Returns a list of search aggregate attributes by incoming state props.
   *              Applies nested aggregate attrs in case People tab was selected.
   * @param { MapState } state
   * @returns { SearchAggregateAttributes[] }
   */
  private static getAggregateAttributes(state: MapState): SearchAggregateAttributes[] {
    const attrs: SearchAggregateAttributes[] = [{ name: SearchAggregationBy.CONTINENT, value: {} }];
    if (isInAggregationCountryZoomRange(state.zoom)) {
      attrs[0].nested_aggregate_attributes = [
        {
          name: SearchAggregationBy.COUNTRY,
          value: {},
        },
      ];
    } else if (isInAggregationRegionZoomRange(state.zoom)) {
      attrs[0].nested_aggregate_attributes = [
        {
          name: SearchAggregationBy.COUNTRY,
          value: {},
          nested_aggregate_attributes: [
            {
              name: SearchAggregationBy.REGION,
              value: {},
            },
          ],
        },
      ];
    } else if (isInAggregationSubregionZoomRange(state.zoom)) {
      attrs[0].nested_aggregate_attributes = [
        {
          name: SearchAggregationBy.COUNTRY,
          value: {},
          nested_aggregate_attributes: [
            {
              name: SearchAggregationBy.REGION,
              value: {},
              nested_aggregate_attributes: [
                {
                  name: SearchAggregationBy.SUBREGION,
                  value: {},
                },
              ],
            },
          ],
        },
      ];
    }

    if (state.filter === MapFilters.PEOPLE) {
      this.addSearchPeopleAggregateAttributes(attrs, state);
    }

    if (state.filter === MapFilters.ORGANIZATIONS) {
      this.addSearchOrganizationAggregateAttributes(attrs);
    }

    return attrs;
  }

  private static nestedPeopleAggregateAttributes(state: MapState): SearchAggregateAttributes {
    return {
      name: MapSettings.PEOPLE_TAB_NESTED_AGGREGATE_ATTR_NAME,
      value: { types: this.getRoleRelatedItems(state, true) },
    };
  }

  private static addSearchPeopleAggregateAttributes(attrs: SearchAggregateAttributes[], state: MapState): void {
    if ('nested_aggregate_attributes' in attrs[0]) {
      attrs[0].nested_aggregate_attributes.push(this.nestedPeopleAggregateAttributes(state));
      this.addSearchPeopleAggregateAttributes(attrs[0].nested_aggregate_attributes, state);
    } else {
      attrs[0].nested_aggregate_attributes = [this.nestedPeopleAggregateAttributes(state)];
    }
  }

  private static nestedOrganizationsAggregateAttributes(): SearchAggregateAttributes {
    return {
      name: MapSettings.ORGANIZATION_TAB_NESTED_AGGREGATE_ATTR_NAME,
      value: { types: MapValues.mapOrganizationTypesKeys },
    };
  }

  private static addSearchOrganizationAggregateAttributes(attrs: SearchAggregateAttributes[]): void {
    if ('nested_aggregate_attributes' in attrs[0]) {
      attrs[0].nested_aggregate_attributes.push(this.nestedOrganizationsAggregateAttributes());
      this.addSearchOrganizationAggregateAttributes(attrs[0].nested_aggregate_attributes);
    } else {
      attrs[0].nested_aggregate_attributes = [this.nestedOrganizationsAggregateAttributes()];
    }
  }
  /**
   * @description Returns the corresponding search attributes depend on the passed list content.
   * @param { string } name
   * @param { MapState } state
   * @returns { SearchAttributes | null }
   */
  private static getDiseasesSearchAttr(name: string, state: MapState): SearchAttributes | null {
    if (
      state.filter === MapFilters.PEOPLE &&
      state.roles &&
      state.roles.includes(NestedBucketIndividualRole.MED_STAFF)
    ) {
      return null;
    }

    return state.diseases && state.diseases.length ? { name, operator: Operator.Eq, values: state.diseases } : null;
  }

  /**
   * @description Returns the corresponding search attributes depend on the passed list content.
   * @param { string } name
   * @param { MapState } state
   * @returns { SearchAttributes | null }
   */
  private static getSpecialtiesAttr(name: string, state: MapState): SearchAttributes | null {
    if (state.filter === MapFilters.PEOPLE && state.roles && state.roles.includes(NestedBucketIndividualRole.PATIENT)) {
      return null;
    }

    return state.specialties && state.specialties.length
      ? { name, operator: Operator.Eq, values: state.specialties }
      : null;
  }

  private static getCharityFocusAttr(name: string, state: MapState): SearchAttributes | null {
    return state.filter === MapFilters.COMMUNITIES && state && state.charity_focuses?.length
      ? { name, operator: Operator.Eq, values: state.charity_focuses }
      : null;
  }

  private static getSearchItems(state: MapState): SearchAttributes {
    return {
      name: CommonMapSearchValues.SEARCH_ATTRIBUTE_SEARCH_ITEMS,
      operator: Operator.Eq,
      values: state.filter !== MapFilters.PEOPLE ? [state.filter] : this.getRoleRelatedItems(state),
    };
  }

  private static getExcludeEmptyLocationFilter(): SearchAttributes {
    return {
      name: CommonMapSearchValues.SEARCH_ATTRIBUTE_EXCLUDE_LOCATION,
    };
  }

  private static getSearchAttrs(params: RequestAttributeParams): SearchAttributes[] {
    const searchedQuery: SearchAttributes = params.state.keyword
      ? {
          name: CommonMapSearchValues.SEARCH_ATTRIBUTE_SEARCH_QUERY,
          operator: Operator.Eq,
          values: [params.state.keyword],
        }
      : null;
    return [
      this.getSearchItems(params.state),
      searchedQuery,
      this.getExcludeEmptyLocationFilter(),
      this.getDiseasesSearchAttr(CommonMapSearchValues.SEARCH_ATTRIBUTE_DISEASES, params.state),
      this.getSpecialtiesAttr(CommonMapSearchValues.SEARCH_ATTRIBUTE_SPECIALITIES, params.state),
      this.getCharityFocusAttr(CommonMapSearchValues.SEARCH_ATTRIBUTE_CHARITY_FOCUSES, params.state),
      ...this.getLocationDetailsAttrs({
        state: params.state,
        applyLocationAttrs: params.applyLocationAttrs,
        aggregated: params.aggregated,
        locationLiteral: params.locationLiteral,
      }),
      ...this.getLocationLiteralAttrs(params.locationLiteral),
    ].filter(Boolean);
  }

  /**
   * @description Returns search items depend on roles property value.
   * @param { MapState } state
   * @param { boolean } forNestedAttrs
   * @returns { string[] }
   */
  private static getRoleRelatedItems(state: MapState, forNestedAttrs?: boolean): string[] {
    const roles: string[] = this.filterRolesRelatedItems(state);
    if (roles && roles.length) {
      return forNestedAttrs
        ? roles
        : roles.map((role: string) => MarkersMappers.nestedBucketIndividualNameToSearchCategoryMapper[role] as string);
    }

    return forNestedAttrs ? MapValues.mapIndividualRolesKeys : [SearchCategories.INDIVIDUALS];
  }

  private static filterRolesRelatedItems(state: MapState): string[] {
    let roles: string[] | undefined = state.roles;
    if ((state.diseases && state.diseases.length > 0) || (state.specialties && state.specialties.length > 0)) {
      if (roles === undefined || roles.length === 0) {
        roles = Object.values(NestedBucketIndividualRole) as string[];
      }
      if (
        state.diseases &&
        state.diseases.length > 0 &&
        state.roles &&
        !state.roles.includes(NestedBucketIndividualRole.MED_STAFF)
      ) {
        roles = roles.filter((role: string) => role !== NestedBucketIndividualRole.MED_STAFF);
      }
      if (
        state.specialties &&
        state.specialties.length > 0 &&
        state.roles &&
        !state.roles.includes(NestedBucketIndividualRole.PATIENT)
      ) {
        roles = roles.filter((role: string) => role !== NestedBucketIndividualRole.PATIENT);
      }
    }
    return roles;
  }

  /**
   * @description Returns location related attrs value with calculated radius depend on
   *              roles prop content.
   * @param { LocationDetailsParams } params
   * @returns { SearchAttributes[] }
   */
  private static getLocationDetailsAttrs(params: LocationDetailsParams): SearchAttributes[] {
    if (!params.applyLocationAttrs || (params.aggregated && !params.state.form_location) || params.locationLiteral) {
      return [];
    }

    return this.getGeoBoundingBoxAttr(params.state.location);
  }

  private static getGeoBoundingBoxAttr(location: MapLocation): SearchAttributes[] {
    return [
      {
        name: CommonMapSearchValues.SEARCH_ATTRIBUTE_GEO_BOUNDING_BOX,
        operator: Operator.Eq,
        body: {
          top_left: location.northWestCoords,
          bottom_right: location.southEastCoords,
        },
      },
    ];
  }

  private static getLocationLiteralAttrs(locationLiteral?: string): SearchAttributes[] {
    if (!locationLiteral) {
      return [];
    }

    return this.getLocationLiteralAttr(locationLiteral);
  }

  private static getLocationLiteralAttr(locationLiteral: string): SearchAttributes[] {
    return [
      {
        name: CommonMapSearchValues.SEARCH_ATTRIBUTE_LOCATION_LITERAL,
        operator: Operator.Eq,
        values: [locationLiteral],
      },
    ];
  }
}
